// DEPRECATED - use channel specific files
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  bool, func, oneOfType, shape, string
} from 'prop-types';
import axios from 'axios';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import Helper from './Helper';
import './media-gallery.style.scss';
import { componentReady } from '../publisher';
import { MediaGalleryDesktop, MediaGalleryMobile } from './subcomponents';
import { MediaHighlight } from './subcomponents/MediaHighlight/MediaHighlight';
import { dataModel } from './dataModel';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';

export const MediaGallery = ({
  collectionId,
  hideProductImagesLabel,
  highlight,
  highRes,
  hover,
  itemId,
  marqueeRef,
  marqueeRowRef,
  media,
  onMediaClick,
  showFavorites,
  showLoader,
  firstItemFetchPriority
}) => {
  const { externalMedia } = useLifeCycleEventBus('configurator.configurator_media_gallery_event');
  const mediaMapRef = useRef(null);
  const [highResImages, setHighResImages] = useState([]);
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';
  const storeId = useStoreId();
  const [hoverId, setHoverId] = useState('');
  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setHoverId(hoverItemId?.output);
      });
    }
  }, [hover]);

  const [hoveredProductRequest, hoveredProductResponse] = useLazyDataModel('product', {
    variables: {
      itemId: hoverId,
      storeId,
    },
  });
  useEffect(() => {
    if (hoverId) {
      hoveredProductRequest({ variables: { hoverId } });
    }
  }, [hoverId, hoveredProductRequest]);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, [itemId]);

  const productData = useDataModel('product', { variables: { itemId }, skip: !itemId });
  const collectionData = useDataModel('collection', { variables: { collectionId, storeId }, skip: !collectionId });

  const { product } = productData?.data || {};
  const { collection } = collectionData?.data || {};
  const { metadata } = collection || {};

  // for swatch Hover
  const mainHover = Helper.getMainHover({
    hoveredProductResponse, collectionData, overrideData: { media }, externalMedia
  });
  const { displayedMedia, ...apiResults } = Helper.getDisplayedMedia({
    isMobile,
    itemId,
    productData,
    collectionId,
    collectionData,
    externalMedia,
    overrideData: { media },
    placeholder: showLoader && <MediaGalleryLoader isMobile={isMobile} />
  });
  const { zoom } = displayedMedia;

  useEffect(() => {
    // TODO: Hook
    if (channel !== 'mobile' && highRes && zoom) {
      const checkValidityPromise = [];
      zoom.forEach((zoomedImage) => {
        const imageCheckPromise = Helper.getHighResImages(zoomedImage, axios);
        checkValidityPromise.push(imageCheckPromise);
      });
      Promise.all(checkValidityPromise).then((responses) => {
        const images = responses.filter((resp) => !!resp);
        if (images.length) {
          setHighResImages(images);
        }
      }).catch(() => { });
    }
  }, [itemId, !!zoom]);

  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }

  if (highlight) {
    return <MediaHighlight itemId={itemId} />;
  }

  const handleMediaClick = ({ media: clickedMedia }) => {
    if (onMediaClick) {
      onMediaClick({ media: clickedMedia });
    }
  };

  mediaMapRef.current = displayedMedia;

  if (highResImages) {
    mediaMapRef.current = {
      ...mediaMapRef.current,
      highResolution: highResImages
    };
  }

  return (
    <div>
      {isMobile ? (
        <MediaGalleryMobile
          media={mediaMapRef.current}
          onMediaClick={handleMediaClick}
          product={product || metadata}
          showFavorites={showFavorites}
          firstItemFetchPriority={firstItemFetchPriority}
        />
      ) : (
        <MediaGalleryDesktop
          media={mediaMapRef.current}
          collection={collection}
          product={product}
          totalThumbnailsToShow={6}
          hideProductImagesLabel={hideProductImagesLabel}
          mainHover={mainHover}
          showFavorites={showFavorites}
          marqueeRef={marqueeRef}
          marqueeRowRef={marqueeRowRef}
        /**
         * TODO: since `ReactImageMagnify` doesn't accept a `fetchPriority` prop,
         * once that is replaced we should also pass the `firstItemFetchPriority` here
         * and add it to the `MediaGalleryDesktop` file as well. Also,
         * `ReactImageMagnify` is not maintained, so no reason to create a PR (last
         * merge was over 2 yrs ago) to that external repo
         *  */
        />
      )}
    </div>
  );
};

MediaGallery.displayName = 'MediaGallery';

MediaGallery.propTypes = {
  collectionId: string,
  hideProductImagesLabel: bool,
  highlight: bool,
  highRes: bool,
  hover: bool,
  itemId: string,
  onMediaClick: func,
  marqueeRef: oneOfType([
    func,
    shape({})
  ]),
  marqueeRowRef: oneOfType([
    func,
    shape({})
  ]),
  media: shape({}),
  showLoader: bool,
  showFavorites: bool,
  firstItemFetchPriority: string
};

MediaGallery.defaultProps = {
  collectionId: null,
  hideProductImagesLabel: false,
  highlight: false,
  highRes: false,
  hover: false,
  itemId: null,
  onMediaClick: null,
  marqueeRef: null,
  marqueeRowRef: null,
  media: null,
  showLoader: false,
  showFavorites: false,
  firstItemFetchPriority: ''
};

MediaGallery.dataModel = dataModel;
